import { isBenignNetworkError } from '@/utils/fetch-json';
import { MUTABLE_MESSAGES_NOT_ENABLED_CODE } from './errorMessages';

interface AblyErrorInfo {
  message?: string;
  code: number;
  statusCode: number;
  details?: unknown;
}

const isAblyErrorInfo = (error: unknown): error is AblyErrorInfo =>
  typeof error === 'object' &&
  error !== null &&
  'code' in error &&
  'statusCode' in error &&
  typeof (error as AblyErrorInfo).code === 'number' &&
  typeof (error as AblyErrorInfo).statusCode === 'number';

/**
 * ably-js raises 40170 when its token request to our authUrl fails. A 401
 * (expired session / role denial) or 404 (transient app-membership edge) from
 * that cookie-authed endpoint is an expected auth condition the UI handles as
 * re-auth, not an actionable error. Dropped like the 80003 filter in
 * entrypoints/sentry.ts.
 */
export const isExpectedAuthError = (error: unknown): boolean =>
  isAblyErrorInfo(error) && error.code === 40170 && (error.statusCode === 401 || error.statusCode === 404);

// `@ably/ai-transport`'s `subscribeAndAttach` wraps a failed channel attach into
// its own SessionSubscriptionError (code 104001, statusCode 500). ably-js reports
// a benign connection-lifecycle close with 80017 (connection closed), 80003
// (connection disconnected) or 80002 (connection to server unavailable) — none of
// which are actionable: they are the connection being torn down or briefly
// dropped, not an application fault.
const BENIGN_CONNECTION_CLOSE_CODES = new Set([80017, 80003, 80002]);

/**
 * When onboarding hydrates, `@ably/ai-transport` opens the session channel via
 * `subscribeAndAttach`; if the underlying ably-js connection is closing, dropped
 * or briefly unavailable at that moment the attach rejects and the transport
 * re-wraps it as its own `ErrorInfo` with `code === 104001`
 * (SessionSubscriptionError). The wrapper's `cause` is the original ably-js
 * connection-lifecycle `ErrorInfo`, so a benign close leaks past the
 * entrypoints/sentry.ts filter (which only drops 80003 arriving as an unhandled
 * rejection) on both the code and the mechanism.
 *
 * Kept deliberately narrow: only a 104001 whose `cause` carries one of the
 * benign connection-close codes is suppressed. A 104001 wrapping anything else —
 * an auth or channel-permission refusal — is a real signal and still reports.
 * When the `cause` (or its code) is absent, fall back to ably-js's close wordings
 * in the wrapped message rather than suppressing on the 104001 code alone.
 */
export const isExpectedConnectionClosedError = (error: unknown): boolean => {
  if (!isAblyErrorInfo(error) || error.code !== 104001) return false;
  const cause = (error as { cause?: unknown }).cause;
  if (isAblyErrorInfo(cause)) return BENIGN_CONNECTION_CLOSE_CODES.has(cause.code);
  return /Connection closed|Connection to server temporarily unavailable/i.test(error.message ?? '');
};

/**
 * ably-js raises 93002 when the dashboard channel inspector optimistically
 * attaches with annotation-subscribe against a namespace that does not have
 * Mutable Messages enabled. The inspector already handles the rejection (drops
 * annotation mode and falls back, no user impact), and because the attach is
 * async the rejection re-surfaces as an unhandled rejection. Not actionable, so
 * it is dropped like the 80003 / 40170 filters in entrypoints/sentry.ts —
 * matched by code so a reworded message cannot let it leak.
 */
export const isExpectedAnnotationModeError = (error: unknown): boolean =>
  isAblyErrorInfo(error) && error.code === MUTABLE_MESSAGES_NOT_ENABLED_CODE;

/**
 * Whether a refusal from our own `/api/apps/:id/auth/*` token endpoints is an
 * expected answer rather than a fault. A 401 is a stale session, which ably-js
 * retries after the user re-auths; a 403 is the caller's role, or the account's
 * message-content restriction — a compliance control that applies to every
 * role. All of those are states the UI renders honestly, and reporting them
 * would file an entry on every page load for the accounts they apply to. A 400
 * (a request we built wrong), 404 or 5xx is a real fault and still reports.
 *
 * `isExpectedAuthError` cannot stand in for this: it matches ably-js's own
 * 40170, raised when *ably-js* fetches an authUrl. Where we fetch the token
 * ourselves the code on the wire is our endpoint's — absent on the generic
 * error view, or a string like `message_content_disabled` — so it never matches.
 */
export const isExpectedTokenFetchError = (cause: { statusCode?: number } | null | undefined): boolean =>
  cause?.statusCode === 401 || cause?.statusCode === 403;

/**
 * When ably-js fetches a token from the onboarding authUrl and that endpoint
 * answers 403, ably-js does not retry: it escalates the refusal to a
 * non-recoverable connection failure and rejects the connection outright. On the
 * onboarding hydrate path a 403 there is a lapsed session or a role denial — an
 * expected state the UI handles by starting the conversation fresh, not an
 * actionable fault.
 *
 * This matches the escalation by its message alone, deliberately: a 403 status
 * on a realtime ErrorInfo *anywhere else* (a capability or permission refusal)
 * is still a real signal the shared `captureException` must keep reporting, so
 * this is not wired into `captureException` — only the onboarding hydrate call
 * site, whose sole Ably client uses that authUrl, suppresses on it. The message
 * test is non-anchored (ably-js composes the string, so match a substring) and
 * case-insensitive; it distinguishes the auth-provider refusal from other
 * hydrate failures (network, channel) we do want reported.
 */
export const isExpectedAitAuthProviderError = (error: unknown): boolean =>
  isAblyErrorInfo(error) && /authentication provider returned 403/i.test(error.message ?? '');

/**
 * Sentry can't facet `extra`, so what divides one issue into cases — a status
 * above all — goes in `extraTags`.
 */
export const captureException = (
  error: unknown,
  component: string,
  phase: string,
  extra?: Record<string, unknown>,
  extraTags?: Record<string, string | number>,
): void => {
  if (isExpectedAuthError(error)) return;
  if (isExpectedConnectionClosedError(error)) return;

  // A statusless network `TypeError` (Safari `Load failed`, Chromium `Failed to
  // fetch`, Firefox `NetworkError...`) is almost always an in-flight fetch
  // cancelled when the user navigates away - benign, and with no `.status` no
  // status-based guard can catch it. The LoaderErrorComponent already drops it
  // for loader failures, but the many other callers of this shared helper (chat
  // client, onboarding state, sign-up) had no equivalent, so the same abort
  // still filed issues from those paths. Breadcrumb it centrally so every caller
  // is covered; any network fault that carries an HTTP response arrives as a
  // status error, not a TypeError, and still reports.
  if (isBenignNetworkError(error)) {
    addBreadcrumb(component, `${phase} benign network: ${String(error)}`, extra);
    return;
  }

  // ably-js can reject with `undefined` (a token request that times out at the
  // wrong moment), and Sentry renders that as an issue titled `<unknown>` with
  // no message and no stack - unactionable by construction, so it accrues
  // events forever without anyone being able to triage it. Record the fact as a
  // message, which keeps the component/phase signal and stays greppable,
  // without pretending we captured an exception.
  if (error === null || error === undefined) {
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureMessage(`${component}: ${phase} threw ${String(error)}`, {
        level: 'warning',
        tags: { component, phase, ...extraTags },
        ...(extra && { extra }),
      });
    }
    return;
  }

  if (typeof window !== 'undefined' && window.Sentry) {
    const tags: Record<string, string | number> = { component, phase, ...extraTags };
    const contexts: Record<string, Record<string, unknown>> = {};

    if (isAblyErrorInfo(error)) {
      tags.realtime_error_code = error.code;
      tags.realtime_status_code = error.statusCode;
      contexts.realtime_error_info = {
        message: error.message,
        code: error.code,
        statusCode: error.statusCode,
        details: error.details,
      };
    }

    window.Sentry.captureException(error, {
      tags,
      contexts,
      ...(extra && { extra }),
    });
  }
};

/**
 * Records something that happened, rather than something that failed. For an
 * expected-but-noteworthy branch -- a cohort being routed a particular way, a
 * fallback being taken -- where `captureException` would file a fault that
 * isn't one, and `addBreadcrumb` would be dropped because nothing later in the
 * session captures an event to attach it to.
 *
 * Same tag discipline as `captureException`: `extraTags` is what Sentry can
 * facet, `extra` is what it can only display.
 */
export const captureMessage = (
  message: string,
  component: string,
  phase: string,
  extra?: Record<string, unknown>,
  extraTags?: Record<string, string | number>,
  level: 'info' | 'warning' = 'info',
): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.captureMessage(message, {
      level,
      tags: { component, phase, ...extraTags },
      ...(extra && { extra }),
    });
  }
};

export const addBreadcrumb = (component: string, message: string, data?: Record<string, unknown>): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.addBreadcrumb({ category: component, message, level: 'warning', ...(data && { data }) });
  }
};
